import "../../polyfills";
import "../../base.css";
import "../../turoUiFonts.css";

import { Pedal } from "@turo/pedal";
import type { DriverAccountResponse } from "@turo/types-dunlop";
import { TuroUi } from "@turo/ui";
import { Fragment } from "react";
import { createRoot } from "react-dom/client";

import {
  loginStart,
  loginSuccess,
  otpTokenLoginStart,
} from "~/src/helpers/loginActions";

import {
  dispatch,
  getPropertiesFromQuery,
  getTranslationsLocale,
} from "../../helpers/iframeHelpers";
import SimpleTranslateProvider from "../../SimpleTranslateProvider";
import commonEnUsTranslations from "../locales/en_US.json";
import commonEsUsTranslations from "../locales/es_US.json";
import commonFrCaTranslations from "../locales/fr_CA.json";
import commonFrFrTranslations from "../locales/fr_FR.json";
import commonPtBrTranslations from "../locales/pt_BR.json";
import enUsTranslations from "./locales/en_US.json";
import esUsTranslations from "./locales/es_US.json";
import frCaTranslations from "./locales/fr_CA.json";
import frFrTranslations from "./locales/fr_FR.json";
import ptBrTranslations from "./locales/pt_BR.json";
import PhoneLoginView from "./PhoneLoginView";

export type OtpLoginPhoneTranslations = typeof enUsTranslations &
  typeof frCaTranslations &
  typeof commonEnUsTranslations &
  typeof commonFrCaTranslations;

const { locale } = getPropertiesFromQuery();
// @ts-expect-error -- TODO-TS: Add full type-narrowing handling in getPropertiesFromQuery
const translationsLocale = getTranslationsLocale(locale);

/**
 * TODO: Since `dispatch` is a helper we define rather than a state management library
 * method that is intertwined with re-rendering logic, like `redux` dispatch, we should
 * probably just define and import these methods directly in usage instead of prop-threading.
 */
const mappedPropertiesToDispatch = {
  loginStart: () => dispatch(loginStart()),
  loginSuccess: (data: DriverAccountResponse) => dispatch(loginSuccess(data)),
  otpTokenLoginStart: () => dispatch(otpTokenLoginStart()),
};

function renderPage(translations: OtpLoginPhoneTranslations) {
  const container = document.querySelector("#container");

  /**
   * This is a type guard to satisfying the type definition of `document.querySelector`,
   * but our `index.html` file has a `#container` element, so this will never throw.
   *
   * Common guidance is to use casting (`as Element`) or the non-null assertion operator
   * (`document.querySelector("#container")!`), but both patterns should be discouraged
   * in favor of runtime guards.
   */
  if (!container) {
    throw new Error("Could not find #container element");
  }

  const root = createRoot(container);

  root.render(
    <Fragment>
      <TuroUi>
        {/*
        // @ts-expect-error -- Add runtime type narrowing for locale in getPropertiesFromQuery */}
        <Pedal locale={locale}>
          <SimpleTranslateProvider translations={translations}>
            <PhoneLoginView {...mappedPropertiesToDispatch} />
          </SimpleTranslateProvider>
        </Pedal>
      </TuroUi>
    </Fragment>,
  );
}

let translations;
switch (translationsLocale) {
  case "en_US": {
    translations = { ...commonEnUsTranslations, ...enUsTranslations };
    break;
  }
  case "es_US": {
    translations = { ...commonEsUsTranslations, ...esUsTranslations };
    break;
  }
  case "fr_CA": {
    translations = { ...commonFrCaTranslations, ...frCaTranslations };
    break;
  }
  case "fr_FR": {
    translations = { ...commonFrFrTranslations, ...frFrTranslations };
    break;
  }
  case "pt_BR": {
    translations = { ...commonPtBrTranslations, ...ptBrTranslations };
    break;
  }
  default: {
    translations = { ...commonEnUsTranslations, ...enUsTranslations };
  }
}

renderPage(translations);
